import React, { useState, useRef, useEffect } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../../firebase';
import { JourneyEntry, Goal } from '../types';
import { GoalIcon, CalendarIcon } from '../../../icons';

interface VideoSlotModalProps {
  slot: {
    date: Date;
    dayNumber: number;
    isAvailable: boolean;
    isRecorded: boolean;
    entry?: JourneyEntry;
  };
  goal: Goal; // Goal info for Start video display
  goalId: string;
  ownerId: string;
  onSave: (text: string, videoUrl?: string) => Promise<void>;
  onDelete?: () => Promise<void>;
  onClose: () => void;
}

/**
 * VideoSlotModal - Modal voor video slot opname/weergave
 * 
 * Features:
 * - Video opnemen (max 2 min)
 * - Video afspelen (als opgenomen)
 * - Tekst toevoegen
 * - Verwijderen
 */
const VideoSlotModal: React.FC<VideoSlotModalProps> = ({
  slot,
  goal,
  goalId,
  ownerId,
  onSave,
  onDelete,
  onClose,
}) => {
  const [text, setText] = useState(slot.entry?.textContent || '');
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(slot.entry?.videoUrl || null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaved, setIsSaved] = useState(slot.isRecorded); // Track if video is saved
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  const isStart = slot.dayNumber === 0;
  const title = isStart ? 'Start Video' : `Video Dag ${slot.dayNumber}`;
  const [recordingTime, setRecordingTime] = useState(0);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const MAX_RECORDING_TIME = 5 * 60; // 5 minuten in seconden

  // Effect to attach stream to video element once it's rendered
  useEffect(() => {
    const attachStream = () => {
      if (isRecording && videoRef.current && streamRef.current) {
        // Wacht tot video element klaar is
        videoRef.current.srcObject = streamRef.current;
        videoRef.current.muted = true;
        videoRef.current.play().catch(err => console.warn('Play warning:', err));
      }
    };
    
    // Probeer meteen en na korte delay
    attachStream();
    const timeout = setTimeout(attachStream, 100);
    
    return () => clearTimeout(timeout);
  }, [isRecording]);

  // Recording timer
  useEffect(() => {
    if (isRecording) {
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= MAX_RECORDING_TIME - 1) {
            // Stop recording at max time
            stopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
    } else {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
    
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, [isRecording]);

  const startRecording = async () => {
    try {
      // First cleanup any existing stream/video
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
        videoRef.current.src = '';
        videoRef.current.load();
      }
      
      // Clear previous recording
      setRecordedBlob(null);
      setVideoPreview(null);
      
      // Small delay to ensure cleanup is complete
      await new Promise(resolve => setTimeout(resolve, 100));

      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } }, 
        audio: true 
      });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('video/webm;codecs=vp9') 
          ? 'video/webm;codecs=vp9' 
          : 'video/webm'
      });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        setRecordedBlob(blob);
        const url = URL.createObjectURL(blob);
        setVideoPreview(url);
        
        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
        
        // Auto-save the video immediately after recording stops
        setIsSaving(true);
        try {
          const timestamp = Date.now();
          const videoPath = `journey/${ownerId}/${goalId}/${slot.dayNumber}_${timestamp}.webm`;
          const storageRef = ref(storage, videoPath);
          
          await uploadBytes(storageRef, blob);
          const videoUrl = await getDownloadURL(storageRef);
          
          await onSave(text.trim(), videoUrl);
          setIsSaved(true);
        } catch (err) {
          console.error('Failed to auto-save video:', err);
          alert('Video opslaan mislukt. Probeer handmatig op te slaan.');
        } finally {
          setIsSaving(false);
        }
      };

      // Start recording (record in 1 second chunks for smoother playback)
      mediaRecorder.start(1000);
      
      // Wacht even tot stream echt loopt, dan pas video tonen
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Set isRecording AFTER stream is ready - the useEffect will attach it to the video element
      setIsRecording(true);

      // Auto-stop after 5 minutes (backup - timer effect handles this too)
      setTimeout(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
          stopRecording();
        }
      }, MAX_RECORDING_TIME * 1000);

    } catch (err) {
      console.error('Failed to start recording:', err);
      alert('Kon camera niet starten. Controleer je browser-permissies.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const clearVideo = () => {
    // Cleanup existing video
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
      videoRef.current.src = '';
      videoRef.current.load();
    }
    // Cleanup stream if any
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setRecordedBlob(null);
    setVideoPreview(null);
  };

  const handleSave = async () => {
    if (isSaving) return;
    
    setIsSaving(true);
    try {
      let videoUrl: string | undefined;
      
      // Upload video to Firebase Storage if we have a recording
      if (recordedBlob) {
        const timestamp = Date.now();
        const videoPath = `journey/${ownerId}/${goalId}/${slot.dayNumber}_${timestamp}.webm`;
        const storageRef = ref(storage, videoPath);
        
        await uploadBytes(storageRef, recordedBlob);
        videoUrl = await getDownloadURL(storageRef);
      }
      
      await onSave(text.trim(), videoUrl);
    } catch (err) {
      console.error('Failed to save:', err);
      alert('Opslaan mislukt. Probeer het opnieuw.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete || isDeleting) return;
    if (!window.confirm('Weet je zeker dat je deze video wilt verwijderen?')) return;
    
    setIsDeleting(true);
    try {
      await onDelete();
    } catch (err) {
      console.error('Failed to delete:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    // Stop any active recording
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    // Stop stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    onClose();
  };

  // Close on backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90dvh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500">
              {slot.date.toLocaleDateString('nl-NL', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Video section */}
          <div className="space-y-3">
            {!videoPreview && !isRecording && (
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <button
                  onClick={startRecording}
                  className="flex flex-col items-center gap-2 mx-auto text-gray-600 hover:text-orange-600 transition-colors"
                >
                  <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center">
                    <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium">Start opname</span>
                  <span className="text-xs text-gray-400">Max 5 minuten</span>
                </button>
              </div>
            )}

            {isRecording && (
              <div className="space-y-3">
                <div className="relative">
                  <video 
                    ref={videoRef} 
                    className="w-full rounded-lg bg-black aspect-video"
                    muted
                    playsInline
                    autoPlay
                    style={{ transform: 'scaleX(-1)' }} /* Mirror for selfie view */
                  />
                  {/* Recording indicator overlay */}
                  <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/50 rounded-full px-3 py-1">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    <span className="text-white text-xs">REC</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-red-500 text-sm">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    <span className="font-mono">
                      {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                    </span>
                    <span className="text-gray-400">/ {MAX_RECORDING_TIME / 60}:00</span>
                  </span>
                  <button
                    onClick={stopRecording}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600 transition-colors"
                  >
                    Stop opname
                  </button>
                </div>
              </div>
            )}

            {videoPreview && !isRecording && (
              <div className="space-y-3">
                <video 
                  key={videoPreview} /* Force remount on src change */
                  src={videoPreview}
                  className="w-full rounded-lg bg-black aspect-video"
                  controls
                  playsInline
                />
                {/* Status indicator */}
                <div className="flex justify-between items-center">
                  {isSaving ? (
                    <span className="flex items-center gap-2 text-orange-500 text-sm">
                      <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
                      Video opslaan...
                    </span>
                  ) : isSaved ? (
                    <span className="flex items-center gap-2 text-green-600 text-sm font-medium">
                      <span className="text-green-500">✓</span>
                      Opgeslagen
                    </span>
                  ) : (
                    <button
                      onClick={clearVideo}
                      className="text-sm text-gray-500 hover:text-red-500 transition-colors"
                    >
                      Video verwijderen
                    </button>
                  )}
                  {!isSaving && !isSaved && (
                    <button
                      onClick={startRecording}
                      className="text-sm text-orange-600 hover:text-orange-700 transition-colors"
                    >
                      Opnieuw opnemen
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Goal info (Start video) or Text input (Reflection video) */}
          {isStart ? (
            // Start video: Show goal info (read-only)
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Je doel:
                </label>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-1">
                    <GoalIcon size={14} className="inline flex-shrink-0" /> {goal.title}
                  </h4>
                  {goal.description && (
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">
                      {goal.description}
                    </p>
                  )}
                  <div className="mt-3 pt-3 border-t border-orange-200 text-xs text-gray-500">
                    <CalendarIcon size={12} className="inline" /> {new Date(goal.startDate).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })} 
                    {' → '}
                    {new Date(goal.endDate).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Reflection video: Text input
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Waar sta je nu?
              </label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Wat is er veranderd? Hoe voel je je?"
                rows={4}
                disabled={isSaving || slot.isRecorded}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-50"
              />
            </div>
          )}

          {/* Recorded entry info */}
          {slot.entry && (
            <div className="bg-orange-50 rounded-lg p-3 text-sm">
              <p className="text-orange-800">
                Opgenomen op {new Date(slot.entry.createdAt).toLocaleDateString('nl-NL', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <div>
            {(slot.isRecorded || isSaved) && onDelete && (
              <button
                onClick={handleDelete}
                disabled={isDeleting || isSaving}
                className="text-sm text-red-600 hover:text-red-700 disabled:opacity-50"
              >
                {isDeleting ? 'Verwijderen...' : 'Verwijderen'}
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleClose}
              disabled={isSaving}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
            >
              {(slot.isRecorded || isSaved) ? 'Sluiten' : 'Annuleren'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoSlotModal;
